import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function DayMonthPicker({ value, onChange, tid }) {
  const [mm, dd] = (value || "05-01").split("-");
  const set = (m, d) => {
    const mi = parseInt(m), di = parseInt(d);
    const dim = new Date(2001, mi, 0).getDate();
    onChange(`${String(mi).padStart(2, "0")}-${String(Math.min(di, dim)).padStart(2, "0")}`);
  };
  const selCls = "h-10 rounded-md border border-input bg-white px-2 text-sm w-full";
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2">
      <select data-testid={`${tid}-day`} value={parseInt(dd)} onChange={(e) => set(mm, e.target.value)} className={selCls}>
        {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
      </select>
      <select data-testid={`${tid}-month`} value={parseInt(mm)} onChange={(e) => set(e.target.value, dd)} className={selCls}>
        {MONTHS_FR.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
      </select>
    </div>
  );
}

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setCfg(r.data)); }, []);

  if (!cfg) return <p className="text-slate-400" data-testid="settings-loading">Chargement…</p>;

  const setBooth = (i, v) => setCfg({ ...cfg, booth_names: cfg.booth_names.map((n, j) => (j === i ? v : n)) });
  const setPrice = (id, v) => setCfg({ ...cfg, formulas: cfg.formulas.map((f) => (f.id === id ? { ...f, price: parseFloat(v) || 0 } : f)) });
  const setCost = (id, key, v) => setCfg({ ...cfg, formulas: cfg.formulas.map((f) => (f.id === id ? { ...f, [key]: parseFloat(v) || 0 } : f)) });
  const setChecklist = (k, text) => setCfg({ ...cfg, checklists: { ...cfg.checklists, [k]: text.split("\n").map((s) => s.trim()).filter(Boolean) } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        booth_names: cfg.booth_names,
        formulas: cfg.formulas,
        max_standard_min: parseFloat(cfg.max_standard_min) || 0,
        detour_ok_min: parseFloat(cfg.detour_ok_min) || 0,
        detour_study_min: parseFloat(cfg.detour_study_min) || 0,
        near_min: parseFloat(cfg.near_min) || 0,
        mid_min: parseFloat(cfg.mid_min) || 0,
        km_inclus: parseFloat(cfg.km_inclus) || 0,
        tarif_km: parseFloat(cfg.tarif_km) || 0,
        max_standard_min_summer: parseFloat(cfg.max_standard_min_summer) || 0,
        max_standard_min_winter: parseFloat(cfg.max_standard_min_winter) || 0,
        season_high_start: cfg.season_high_start || "05-01",
        season_high_end: cfg.season_high_end || "09-30",
        last_minute_days: parseInt(cfg.last_minute_days) || 0,
        contact_phone: (cfg.contact_phone || "").trim(),
        tax_rate: parseFloat(cfg.tax_rate) || 0,
        urssaf_rate: parseFloat(cfg.urssaf_rate) || 0,
        versement_rate: parseFloat(cfg.versement_rate) || 0,
        booth_700_id: parseInt(cfg.booth_700_id) || 0,
        stephane_cost: parseFloat(cfg.stephane_cost) || 0,
        stephane_radius_min: parseFloat(cfg.stephane_radius_min) || 15,
        notification_email: (cfg.notification_email || "").trim(),
        pickup_slots: (Array.isArray(cfg.pickup_slots) ? cfg.pickup_slots : String(cfg.pickup_slots || "").split("\n")).map((s) => s.trim()).filter(Boolean),
        cabine_price: parseFloat(cfg.cabine_price) || 0,
        agb_stock: parseInt(cfg.agb_stock) || 0,
        agb_price: parseFloat(cfg.agb_price) || 0,
        checklists: cfg.checklists,
      });
      toast.success("Paramètres enregistrés");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Paramètres</h1>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Noms des photobooths</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cfg.booth_names.map((n, i) => (
            <div key={i} className="space-y-1.5">
              <Label className="text-sm text-slate-600">Machine {i + 1}</Label>
              <Input data-testid={`settings-booth-names-input-${i}`} value={n} onChange={(e) => setBooth(i, e.target.value)} />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Tarifs des formules</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {cfg.formulas.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <Label className="text-sm text-slate-600">{f.label}</Label>
              <Input data-testid={`settings-price-formula-input-${f.id}`} type="number" min="0" step="1" value={f.price} onChange={(e) => setPrice(f.id, e.target.value)} disabled={f.id === "autre"} />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">La formule « Autre » permet de saisir un prix libre dans chaque réservation.</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Règles logistiques</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Temps max habituel depuis Lesménils (min)</Label>
            <Input data-testid="settings-standard-min-input" type="number" min="0" value={cfg.max_standard_min} onChange={(e) => setCfg({ ...cfg, max_standard_min: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Détour acceptable pour une 3e location (min)</Label>
            <Input data-testid="settings-detour-ok-input" type="number" min="0" value={cfg.detour_ok_min} onChange={(e) => setCfg({ ...cfg, detour_ok_min: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Détour maximum à étudier (min)</Label>
            <Input data-testid="settings-detour-study-input" type="number" min="0" value={cfg.detour_study_min} onChange={(e) => setCfg({ ...cfg, detour_study_min: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Très proche de Lesménils : 0 à … min</Label>
            <Input data-testid="settings-near-min-input" type="number" min="0" value={cfg.near_min} onChange={(e) => setCfg({ ...cfg, near_min: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Proche : jusqu'à … min</Label>
            <Input data-testid="settings-mid-min-input" type="number" min="0" value={cfg.mid_min} onChange={(e) => setCfg({ ...cfg, mid_min: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Rayon maximum livraison Stéphane (min)</Label>
            <Input data-testid="settings-stephane-radius-input" type="number" min="0" value={cfg.stephane_radius_min} onChange={(e) => setCfg({ ...cfg, stephane_radius_min: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm text-slate-600">Créneaux rapides de reprise (un par ligne)</Label>
            <textarea
              data-testid="settings-pickup-slots-input"
              rows={3}
              value={(cfg.pickup_slots || []).join("\n")}
              onChange={(e) => setCfg({ ...cfg, pickup_slots: e.target.value.split("\n") })}
              className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
            />
            <p className="text-xs text-slate-400">Format : 11h30 – 12h00 (l'heure de début est utilisée pour le tri des tâches)</p>
          </div>
        </div>
        <p className="text-xs text-slate-400">
          Zones depuis Lesménils : très proche 0–{cfg.near_min || 10} min · proche {cfg.near_min || 10}–{cfg.mid_min || 20} min ·
          zone normale {cfg.mid_min || 20}–{cfg.max_standard_min || 30} min · hors zone au-delà.
          Des réservations existantes très proches de la base rendent la 3e location 🟢 TRÈS FAVORABLE.
          Détour de la 3e location : jusqu'à « acceptable » → 🟢, jusqu'à « maximum à étudier » → 🟠, au-delà → 🔴.
          Les trajets sont calculés en routage routier réel (voiture) depuis Lesménils (54700).
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Supplément kilométrique</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Rayon inclus (km routiers, livraison + reprise)</Label>
            <Input data-testid="settings-km-inclus-input" type="number" min="0" value={cfg.km_inclus} onChange={(e) => setCfg({ ...cfg, km_inclus: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Tarif par km supplémentaire (€)</Label>
            <Input data-testid="settings-tarif-km-input" type="number" min="0" step="0.05" value={cfg.tarif_km} onChange={(e) => setCfg({ ...cfg, tarif_km: e.target.value })} />
          </div>
        </div>
        <p className="text-xs text-slate-400">Formule : (distance aller simple − rayon inclus) × 4 trajets × tarif/km.</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Règles saisonnières (zone habituelle)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Saison haute — du</Label>
            <DayMonthPicker tid="settings-season-high-start" value={cfg.season_high_start} onChange={(v) => setCfg({ ...cfg, season_high_start: v })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Saison haute — au</Label>
            <DayMonthPicker tid="settings-season-high-end" value={cfg.season_high_end} onChange={(v) => setCfg({ ...cfg, season_high_end: v })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Saison haute : temps max (min)</Label>
            <Input data-testid="settings-summer-min-input" type="number" min="0" value={cfg.max_standard_min_summer} onChange={(e) => setCfg({ ...cfg, max_standard_min_summer: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Saison basse : temps max (min)</Label>
            <Input data-testid="settings-winter-min-input" type="number" min="0" value={cfg.max_standard_min_winter} onChange={(e) => setCfg({ ...cfg, max_standard_min_winter: e.target.value })} />
          </div>
        </div>
        <p className="text-xs text-slate-400">La saison basse correspond automatiquement au reste de l'année. Par défaut : saison haute du 1er mai au 30 septembre (25 min), saison basse du 1er octobre au 30 avril (30 min).</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Demandes internet de dernière minute</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Délai minimum pour confirmation automatique en ligne (jours avant l'événement)</Label>
            <Input data-testid="settings-last-minute-days" type="number" min="0" max="30" value={cfg.last_minute_days} onChange={(e) => setCfg({ ...cfg, last_minute_days: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Téléphone professionnel affiché (bouton NOUS APPELER)</Label>
            <Input data-testid="settings-contact-phone" value={cfg.contact_phone} onChange={(e) => setCfg({ ...cfg, contact_phone: e.target.value })} placeholder="06 81 84 62 23" />
          </div>
        </div>
        <p className="text-xs text-slate-400">Avec 2 jours : J-0, J-1 et J-2 affichent « 🟠 Demande de dernière minute — à confirmer » (sans réservation ni paiement en ligne), même si le matériel est disponible. L'application interne n'est pas concernée : vous pouvez toujours créer une réservation manuellement.</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Rentabilité (micro-entreprise)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Prélèvements (% du CA)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="text-xs text-slate-500">URSSAF (%)</span>
                <Input data-testid="settings-urssaf-rate-input" type="number" min="0" step="0.1" value={cfg.urssaf_rate ?? ""} onChange={(e) => setCfg({ ...cfg, urssaf_rate: e.target.value })} />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Versement libératoire (%)</span>
                <Input data-testid="settings-versement-rate-input" type="number" min="0" step="0.1" value={cfg.versement_rate ?? ""} onChange={(e) => setCfg({ ...cfg, versement_rate: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1">Total appliqué automatiquement : <strong data-testid="settings-tax-total" className="text-slate-700">{((parseFloat(cfg.urssaf_rate) || 0) + (parseFloat(cfg.versement_rate) || 0)).toFixed(1)} %</strong></p>
          </div>
        </div>
        <p className="text-xs text-slate-400">Par défaut 23 % = URSSAF 21,2 % + versement libératoire de l'impôt 1,7 %.</p>
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700">Coûts directs par formule (€)</p>
          {cfg.formulas.filter((f) => f.id !== "autre").map((f) => (
            <div key={f.id} className="grid grid-cols-4 gap-2 items-end">
              <p className="text-sm text-slate-600 font-medium self-center">{f.label}</p>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Consommable</Label>
                <Input data-testid={`settings-cost-consumable-${f.id}`} type="number" min="0" value={f.consumable} onChange={(e) => setCost(f.id, "consumable", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Frais bancaires</Label>
                <Input data-testid={`settings-cost-bank-${f.id}`} type="number" min="0" value={f.bank_fee} onChange={(e) => setCost(f.id, "bank_fee", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Accessoires</Label>
                <Input data-testid={`settings-cost-accessories-${f.id}`} type="number" min="0" value={f.accessories} onChange={(e) => setCost(f.id, "accessories", e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Matériel</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Coût par défaut Stéphane (€ / trajet)</Label>
            <Input data-testid="settings-stephane-cost-input" type="number" min="0" value={cfg.stephane_cost} onChange={(e) => setCfg({ ...cfg, stephane_cost: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Cabine Audio Guest Book (€ / événement)</Label>
            <Input data-testid="settings-cabine-price-input" type="number" min="0" value={cfg.cabine_price} onChange={(e) => setCfg({ ...cfg, cabine_price: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Stock Audio Guest Books (total)</Label>
            <Input data-testid="settings-agb-stock-input" type="number" min="0" value={cfg.agb_stock} onChange={(e) => setCfg({ ...cfg, agb_stock: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Audio Guest Book seul (€ / événement)</Label>
            <Input data-testid="settings-agb-price-input" type="number" min="0" value={cfg.agb_price} onChange={(e) => setCfg({ ...cfg, agb_price: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-600">Email de notification des demandes internet</Label>
            <Input data-testid="settings-notification-email-input" type="email" value={cfg.notification_email || ""} onChange={(e) => setCfg({ ...cfg, notification_email: e.target.value })} />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Check-lists matériel</h2>
        <p className="text-xs text-slate-400">Un élément par ligne. Les listes se génèrent automatiquement selon le photobooth et les options de la réservation.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["booth12", `${cfg.booth_names[0]} + ${cfg.booth_names[1]} — checklist commune`], ["2", `${cfg.booth_names[2]} — spécifique`], ["cabine", "Cabine Audio Guest Book"], ["agb", "Audio Guest Book"]].map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-sm text-slate-600">{label}</Label>
              <textarea
                data-testid={`settings-checklist-${k}`}
                rows={5}
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm font-mono"
                value={(cfg.checklists?.[k] || []).join("\n")}
                onChange={(e) => setChecklist(k, e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      <Button data-testid="settings-save-button" onClick={save} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Enregistrer les paramètres
      </Button>
    </div>
  );
}
